import React from 'react';
import { Container } from './ui';
import { CheckList, formatDate, viewsLabel } from './cards';
import { resolveImg } from '../lib/marketingApi';

// Shared presentational article bodies — used by public pages AND the CMS preview
// so drafts render pixel-identical to what will be published.

export function BlogArticle({ post }) {
  const body = Array.isArray(post.body) ? post.body : [];
  return (
    <div data-testid="blog-article">
      <section className="border-b border-slate-200 bg-slate-50 px-5 py-12 dark:border-white/10 dark:bg-white/[0.02] sm:px-8 sm:py-16">
        <Container className="max-w-3xl">
          {post.category && <span className="inline-block rounded-full bg-brand px-3 py-1 text-xs font-semibold text-white">{post.category}</span>}
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight text-slate-900 dark:text-white sm:text-4xl">{post.title || 'Untitled article'}</h1>
          <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            {post.author && <>By {post.author} · </>}{post.date && <>{formatDate(post.date)} · </>}{post.readTime || post.read_time || 5} min read{post.views ? <> · {viewsLabel(post.views)}</> : null}
          </div>
        </Container>
      </section>

      <section className="px-5 py-12 sm:px-8 sm:py-16">
        <Container className="max-w-3xl">
          {post.image && (
            <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10">
              <img src={resolveImg(post.image)} alt={post.title} className="h-64 w-full object-cover sm:h-80" />
            </div>
          )}
          <div className="mt-10">
            {body.map((sec, i) => (
              <div key={i} className="mb-8">
                {sec.heading && <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white sm:text-2xl">{sec.heading}</h2>}
                <div className="mt-3 space-y-3">
                  {(sec.paragraphs || []).map((p, j) => (<p key={j} className="text-base leading-relaxed text-slate-600 dark:text-slate-300">{p}</p>))}
                </div>
              </div>
            ))}
            {body.length === 0 && <p className="text-sm text-slate-400">No content yet.</p>}
          </div>
        </Container>
      </section>
    </div>
  );
}

function Block({ eyebrow, title, paragraphs }) {
  if (!paragraphs || paragraphs.length === 0) return null;
  return (
    <div className="mb-10">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">{eyebrow}</div>
      <h2 className="mt-2 font-display text-2xl font-bold text-slate-900 dark:text-white">{title}</h2>
      <div className="mt-3 space-y-3">
        {paragraphs.map((p, i) => (<p key={i} className="text-base leading-relaxed text-slate-600 dark:text-slate-300">{p}</p>))}
      </div>
    </div>
  );
}

export function CaseStudyArticle({ cs }) {
  const metrics = Array.isArray(cs.metrics) ? cs.metrics : [];
  return (
    <div data-testid="case-study-article">
      <section className="relative overflow-hidden border-b border-white/10 bg-brand-ink px-5 py-16 sm:px-8 sm:py-20">
        {cs.image && <img src={resolveImg(cs.image)} alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20" />}
        <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-brand/25 blur-[120px]" />
        <Container className="relative">
          {cs.segment && <span className="inline-block rounded-full bg-brand px-3 py-1 text-xs font-semibold text-white">{cs.segment}</span>}
          <h1 className="mt-4 max-w-3xl font-display text-3xl font-extrabold leading-tight text-white sm:text-4xl">{cs.title || 'Untitled case study'}</h1>
          {(cs.profile || cs.views) && <p className="mt-3 text-sm font-medium text-slate-300">{[cs.profile, cs.views ? viewsLabel(cs.views) : null].filter(Boolean).join(' · ')}</p>}
        </Container>
      </section>

      {metrics.length > 0 && (
        <section className="border-b border-slate-200 bg-slate-50 px-5 py-10 dark:border-white/10 dark:bg-white/[0.02] sm:px-8">
          <Container>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {metrics.map((m, i) => (
                <div key={i} className="text-center sm:text-left">
                  <div className="font-display text-2xl font-bold text-brand sm:text-3xl">{m.value}</div>
                  <div className="mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400">{m.label}</div>
                </div>
              ))}
            </div>
          </Container>
        </section>
      )}

      <section className="px-5 py-16 sm:px-8 sm:py-20">
        <Container className="max-w-3xl">
          <Block eyebrow="The challenge" title="Where things stood" paragraphs={cs.challenge} />
          <Block eyebrow="The solution" title="What we put in place" paragraphs={cs.solution} />
          <Block eyebrow="The approach" title="How we delivered" paragraphs={cs.approach} />
          {Array.isArray(cs.results) && cs.results.length > 0 && (
            <div className="mb-10">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">The results</div>
              <h2 className="mt-2 font-display text-2xl font-bold text-slate-900 dark:text-white">Outcomes that matter</h2>
              <CheckList className="mt-5" items={cs.results} />
            </div>
          )}
          {cs.quote && cs.quote.text && (
            <blockquote className="mb-10 rounded-2xl border border-slate-200 bg-slate-50 p-7 dark:border-white/10 dark:bg-white/[0.03]">
              <p className="font-display text-lg font-medium leading-relaxed text-slate-800 dark:text-slate-100">&ldquo;{cs.quote.text}&rdquo;</p>
              {cs.quote.attribution && <footer className="mt-3 text-sm text-slate-500 dark:text-slate-400">— {cs.quote.attribution}</footer>}
            </blockquote>
          )}
        </Container>
      </section>
    </div>
  );
}
